import { NavigateFunction, Route, Routes, useNavigate, useResolvedPath } from 'react-router-dom';
import { StoreKeyComplete } from './StoreKeyComplete/StoreKeyComplete';
import { UpdateTotalForm } from './UpdateTotal/UpdateTotal';
import { Admin_StoreKeyRoutePaths, AdminRoutePaths, AppRoutePaths, PathName } from '../../../../helpers/Routes';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentStateUnsafe } from '../../../../context/Appointment/appointment';
import { getAppointment, IAppointment } from '../../../../API/GoMoto';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { StoreKeyProps } from './StoreKeyRoutes.types';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { KioskTypeName, LoanerOrRentalOption } from '../../../../API/Enums';
import { StoreKey } from './StoreKey/StoreKey';
import { PaymentCheck } from './PaymentCheck/PaymentCheck';
import { AdminLoanerOrRental } from './AdminLoanerOrRental/AdminLoanerOrRental';
import { AdminContactForm } from './AdminContactForm/AdminContactForm';
import { AdminCustomerForm } from './AdminCustomerForm/AdminCustomerForm';
import { useEffect } from 'react';

/**
 * This component handles rounting for both the Create Pickup appointment and
 * also for the store key process.
 *
 * @param onProceed - {@link StoreKeyProps.onProceed onProceed} description.
 * @param repullAppt - {@link StoreKeyProps.repullAppt repullAppt} description.
 * @param isAdminCreate - {@link StoreKeyProps.isAdminCreate isAdminCreate} description.
 */
export const StoreKeyRoutes: GoMotoComponent<StoreKeyProps> = ({
    onProceed,
    repullAppt,
    isAdminCreate,
}) => {
    const { pathname } = useResolvedPath('.');
    const {
        kioskTypeName,
        dealer: {
            loanerOrRentalOption,
        },
    } = useKioskState();
    const navigate: NavigateFunction = useNavigate();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const appointmentDispatch = useAppointmentDispatch();

    const isExpressOrThe365: boolean = kioskTypeName === KioskTypeName.TheExpress || kioskTypeName === KioskTypeName.The365;
    const isLoanerOrRentalEnabled: boolean = loanerOrRentalOption !== LoanerOrRentalOption.None && isExpressOrThe365;

    useEffect(() => {
        if (isAdminCreate) {
            appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
        }
    }, [appointmentDispatch, isAdminCreate]);

    return (
        <Routes>
            <Route
                path={PathName(Admin_StoreKeyRoutePaths.UpdateTotal, '*')}
                element={
                    <UpdateTotalForm
                        onProceed={() => {
                            if (isLoanerOrRentalEnabled) {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.LoanerOrRentalReturn));
                            }
                            else {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.Placekey));
                            }
                        }}
                    />
                }
            />
            <Route
                path={PathName(Admin_StoreKeyRoutePaths.Placekey, '*')}
                element={
                    <StoreKey
                        onProceed={() => {
                            if (!isAdminCreate) {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.Complete));
                            }
                            else {
                                appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                                navigate(PathName(AppRoutePaths.Admin, AdminRoutePaths.KeyManagement), { replace: true });
                            }
                        }}
                        onErrorClose={() => {
                            if (isAdminCreate) {
                                appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                            }
                            onProceed();
                        }}
                    />
                }
            />
            <Route
                path={PathName(Admin_StoreKeyRoutePaths.Complete, '*')}
                element={
                    <StoreKeyComplete
                        onProceed={async () => {
                            if (appointment) {
                                const repullAppointment: IAppointment | null = repullAppt ? await getAppointment(appointment.key) : null;

                                if (repullAppointment !== null) {
                                    appointmentDispatch({ type: AppointmentDispatchTypes.Update,
                                        payload: repullAppointment });
                                }
                                else {
                                    appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                                }
                            }

                            onProceed();
                        }}
                    />
                }
            />
            <Route
                path={PathName(Admin_StoreKeyRoutePaths.PaymentCheck, '*')}
                element={
                    <PaymentCheck
                        onProceed={(paymentRequired = false) => {
                            if (paymentRequired) {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.UpdateTotal));
                            }
                            else if (isLoanerOrRentalEnabled) {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.LoanerOrRentalReturn));
                            }
                            else {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.Placekey));
                            }
                        }}
                        onSkip={() => {
                            if (isLoanerOrRentalEnabled) {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.LoanerOrRentalReturn), { replace: true });
                            }
                            else {
                                navigate(PathName(pathname, Admin_StoreKeyRoutePaths.Placekey), { replace: true });
                            }
                        }}
                    />
                }
            />
            <Route
                path={PathName(Admin_StoreKeyRoutePaths.LoanerOrRentalReturn, '*')}
                element={isLoanerOrRentalEnabled ? (
                    <AdminLoanerOrRental
                        onProceed={() => {
                            navigate(PathName(pathname, Admin_StoreKeyRoutePaths.Placekey));
                        }}
                    />
                ) : null}
            />
            <Route
                path={PathName(Admin_StoreKeyRoutePaths.ContactInformation)}
                element={
                    <AdminContactForm
                        onProceed={() => {
                            navigate(PathName(pathname, Admin_StoreKeyRoutePaths.PaymentCheck));
                        }}
                    />
                }
            />
            <Route
                path={''}
                element={
                    <AdminCustomerForm
                        onProceed={() => {
                            navigate(PathName(pathname, Admin_StoreKeyRoutePaths.ContactInformation));
                        }}
                        isAdminCreate={isAdminCreate}
                    />
                }
            />
        </Routes>
    );
};
